import { useState } from "react";
import { notificationService } from "../../services/notificationService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { AlertCircle } from "lucide-react";

interface InviteOthersProps {
  open: boolean;
  onClose: () => void;
  sender: string;
}

export function InviteOthers({ open, onClose, sender }: InviteOthersProps) {
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("Join Wealth Brain AI");
  const [msgBody, setMsgBody] = useState("You're invited to join Wealth Brain AI, an intelligent platform designed for financial professionals and investors.\n\nCollaborate with advisors, analysts, and investors. Share insights, manage portfolios, schedule meetings, and leverage AI-powered tools \u2014 all in one secure environment.\n\nGet started at: app.wealthbrainai.com\nLearn more: wealthbrainai.com\n\nFor questions, reach out to us at info@wealthbrainai.com.\n\nWe look forward to having you on board.\n\n\u2014 The Wealth Brain AI Team");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInvite = async () => {
    if (!recipients.trim()) {
      setError("Please enter at least one recipient email");
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      await notificationService.sendEmail(recipients, subject, msgBody, sender);
      setSuccess(true);
      setRecipients("");
      setSubject("Join Wealth Brain AI");
      setMsgBody("You're invited to join Wealth Brain AI, an intelligent platform designed for financial professionals and investors.\n\nCollaborate with advisors, analysts, and investors. Share insights, manage portfolios, schedule meetings, and leverage AI-powered tools \u2014 all in one secure environment.\n\nGet started at: app.wealthbrainai.com\nLearn more: wealthbrainai.com\n\nFor questions, reach out to us at info@wealthbrainai.com.\n\nWe look forward to having you on board.\n\n\u2014 The Wealth Brain AI Team");
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send invitation");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) { onClose(); setError(null); setSuccess(false); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite to Wealth Brain AI</DialogTitle>
          <DialogDescription>
            Send an invitation email to join the Wealth Brain AI platform
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="recipients">Recipients Email (comma separated)</Label>
            <Input
              id="recipients"
              placeholder="john@example.com, jane@example.com"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="Join Wealth Brain AI"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="body">Body</Label>
            <Textarea
              id="body"
              placeholder="Enter your invitation message..."
              value={msgBody}
              onChange={(e) => setMsgBody(e.target.value)}
              rows={5}
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg p-3">
              Invitation sent successfully!
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={isSending || !recipients.trim()}>
            {isSending ? "Sending..." : "Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
